import React, { useEffect } from 'react';
import { createEvent } from 'ics';
import { decodeBase64Object } from 'shared/common';

const DownloadCalendar = () => {
  // Parse query parameters
  const parseQueryParams = () => {
    const urlParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    );
    const decodedParams = decodeBase64Object(urlParams);

    return {
      title: decodedParams.title || '',
      description: decodedParams.description || '',
      location: decodedParams.location || '',
      startDate: decodedParams.startDate || '',
      startTime: decodedParams.startTime || '',
      filename: decodedParams.filename || 'event.ics',
      categories: decodedParams.categories
        ? decodedParams.categories.split(',')
        : [],
      url: decodedParams.url || '',
    };
  };

  // Convert date and time to the format required by ics library
  const parseDateTime = (dateStr, timeStr) => {
    try {
      const [year, month, day] = dateStr.split(/[-\/]/).map(Number);
      const [hours, minutes] = timeStr.split(':').map(Number);

      // ics library expects [year, month, day, hour, minute]
      return [year, month, day, hours, minutes];
    } catch (error) {
      console.error('Error parsing date/time:', error);
      return null;
    }
  };

  // Generate and download ICS file
  const generateCalendarFile = async (eventParams) => {
    try {
      const startDateTime = parseDateTime(
        eventParams.startDate,
        eventParams.startTime
      );

      if (!startDateTime) {
        throw new Error('Invalid date or time format');
      }

      // Create event lasting 1 hour by default
      const endDateTime = [...startDateTime];
      endDateTime[3] = endDateTime[3] + 1; // Add 1 hour

      const event = {
        title: eventParams.title,
        description: eventParams.description,
        location: eventParams.location,
        start: startDateTime,
        end: endDateTime,
        categories: eventParams.categories,
        status: 'CONFIRMED',
        busyStatus: 'BUSY',
        ...(eventParams.url && { url: eventParams.url }),
      };

      const { error, value } = createEvent(event);

      if (error) {
        console.error('Error creating event:', error);
        throw new Error('Failed to create calendar event');
      }

      // Create and download the file
      const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = eventParams.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating calendar file:', error);
    }
  };

  useEffect(() => {
    const params = parseQueryParams();

    // Validate required parameters
    if (!params.title || !params.startDate || !params.startTime) {
      console.error('Missing required calendar parameters');
      return;
    }

    // Start download immediately
    generateCalendarFile(params);
  }, []);

  // Return nothing - no visual elements
  return null;
};

export default DownloadCalendar;
